import { useEffect } from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from '../../lib/i18n/LanguageContext';
import SEO from '../../components/SEO';
import Hero from './Hero';
import AboutPreview from './AboutPreview';
import MenuPreview from './MenuPreview';
import Testimonials from './Testimonials';
import ReservationForm from '../../components/forms/ReservationForm';
import SectionTitle from '../../components/ui/SectionTitle';

const Home = () => {
  const { t } = useLanguage();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [t]);

  // Structured data for homepage
  const structuredData = [
    {
      '@context': 'https://schema.org',
      '@type': 'Restaurant',
      name: 'Rossopomodoro Reykjavik',
      alternateName: 'Best Italian Restaurant Reykjavik',
      description:
        'Authentic Italian restaurant in downtown Reykjavik serving traditional Neapolitan pizza, fresh pasta, and Italian cuisine since 2006. Located on Laugavegur with wood-fired oven.',
      image: 'https://rossopomodoro.is/images/hero11.jpg',
      url: 'https://rossopomodoro.is',
      telephone: '+354 561 0500',
      email: 'larusg@simnet.is',
      address: {
        '@type': 'PostalAddress',
        streetAddress: 'Laugavegi 40a',
        addressLocality: 'Reykjavík',
        postalCode: '101',
        addressCountry: 'IS',
        addressRegion: 'Capital Region',
      },
      geo: {
        '@type': 'GeoCoordinates',
        latitude: 64.14469761638455,
        longitude: -21.928471483883666,
      },
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue: '4.5',
        reviewCount: '150',
        bestRating: '5',
        worstRating: '1',
      },
      openingHoursSpecification: [
        {
          '@type': 'OpeningHoursSpecification',
          dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
          opens: '12:00',
          closes: '22:00',
        },
        {
          '@type': 'OpeningHoursSpecification',
          dayOfWeek: 'Sunday',
          opens: '17:00',
          closes: '22:00',
        },
      ],
      servesCuisine: ['Italian', 'Neapolitan', 'Pizza', 'Pasta', 'Mediterranean'],
      priceRange: '$$',
      paymentAccepted: ['Cash', 'Credit Card', 'Debit Card'],
      currenciesAccepted: 'ISK',
      menu: 'https://rossopomodoro.is/menu',
      acceptsReservations: 'True',
      hasMenu: 'https://rossopomodoro.is/menu',
      founder: {
        '@type': 'Organization',
        name: 'Rossopomodoro Group',
        foundingDate: '1997',
        foundingLocation: 'Naples, Italy',
      },
      foundingDate: '2006',
      keywords:
        'Italian restaurant, Neapolitan pizza, authentic Italian food, wood-fired oven, downtown Reykjavik, Laugavegur',
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: 'Home',
          item: 'https://rossopomodoro.is/',
        },
      ],
    },
  ];
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.4 }}
    >
      <SEO
        title="Rossopomodoro | Authentic Neapolitan Pizza & Pasta"
        description="Authentic Italian restaurant in downtown Reykjavik. Best Neapolitan pizza, fresh pasta & Italian wines. Wood-fired oven since 2006. Book your table at Rossopomodoro on Laugavegur."
        canonical="https://rossopomodoro.is/"
        keywords="Italian restaurant Reykjavik, best pizza Reykjavik, Neapolitan pizza, authentic Italian food, wood-fired pizza, pasta restaurant, Italian wine, downtown Reykjavik, Laugavegur restaurant"
        ldjson={structuredData}
      />
      <Hero />
      <AboutPreview />
      {/* <InfoOverview /> */}
      <MenuPreview />
      <Testimonials />
      <section id="reservation" className="py-20 bg-red-50">
        <div className="container-custom">
          <SectionTitle title={t.home.reservation.title} subtitle={t.home.reservation.subtitle} />

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-10 px-4 sm:px-0">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="w-full max-w-none lg:max-w-full"
            >
              <ReservationForm showStepperOnMobile={true} />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="w-full"
            >
              <div className="h-full relative rounded-lg shadow-md overflow-hidden min-h-[400px] lg:min-h-[600px]">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d1739.959338569762!2d-21.928471483883666!3d64.14469761638455!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x48d674c7c735704b%3A0xcddb30a944ea193b!2sRossopomodoro!5e0!3m2!1sen!2sus!4v1749820966602!5m2!1sen!2sus"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  title="Rossopomodoro Restaurant Location in downtown Reykjavik"
                  className="absolute inset-0"
                />
                <div className="absolute bottom-2 left-2 sm:bottom-4 sm:left-4 bg-white px-2 sm:px-4 py-1 sm:py-2 rounded-lg shadow-md flex items-center gap-1 sm:gap-2">
                  <svg
                    className="w-5 h-5 text-red-800"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                  </svg>
                  <a
                    href="https://maps.app.goo.gl/iJ38nHTkanTaXeiS8"
                    target="_blank"
                    rel="nofollow noopener noreferrer"
                    className="text-sm text-neutral-700 hover:text-red-800 transition-colors"
                    aria-label="View Rossopomodoro location on Google Maps - Best Italian restaurant in downtown Reykjavik"
                  >
                    Laugavegi 40a, 101 Reykjavík
                  </a>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </motion.div>
  );
};

export default Home;
